import { Lock, Award, Zap, Target, Flame, Skull, Crown, Ghost, Repeat, Trophy, TrendingUp, Rocket } from "lucide-react";

// Rarity styles — each tier gets its own glow + border palette. COD-ish.
const RARITY = {
  bronze: {
    border: "#7A5233",
    glow: "rgba(180, 120, 60, 0.45)",
    ring: "#B4783C",
    text: "#E7B57F",
    label: "COMMON",
  },
  silver: {
    border: "#5E7480",
    glow: "rgba(180, 210, 230, 0.4)",
    ring: "#B4CFE0",
    text: "#DFE9EF",
    label: "RARE",
  },
  gold: {
    border: "#8A6A15",
    glow: "rgba(255, 195, 60, 0.5)",
    ring: "#F5C33C",
    text: "#FFD873",
    label: "EPIC",
  },
  diamond: {
    border: "#1F5F7A",
    glow: "rgba(80, 220, 255, 0.55)",
    ring: "#54DFFF",
    text: "#8AECFF",
    label: "LEGENDARY",
  },
  legendary: {
    border: "#7A1C4B",
    glow: "rgba(255, 90, 180, 0.55)",
    ring: "#FF5FB4",
    text: "#FF9ED2",
    label: "MYTHIC",
  },
};

const ICONS = {
  first_blood: Skull,
  century_dialer: Zap,
  sniper: Target,
  hat_trick: Flame,
  streak_master: Repeat,
  marathon: TrendingUp,
  big_game_hunter: Crown,
  mrr_machine: Rocket,
  untouchable: Trophy,
  ghost_buster: Ghost,
  perfect_month: Award,
  the_franchise: Crown,
};

export function BadgeCard({ badge, size = "md" }) {
  const r = RARITY[badge.rarity] || RARITY.bronze;
  const Icon = ICONS[badge.key] || Award;
  const unlocked = !!badge.unlocked;
  const isSm = size === "sm";

  return (
    <div
      data-testid={`badge-${badge.key}`}
      className={`relative overflow-hidden ${isSm ? "p-2.5" : "p-3"} transition-transform hover:-translate-y-0.5`}
      style={{
        border: `1px solid ${unlocked ? r.border : "#202B30"}`,
        background: unlocked
          ? `linear-gradient(140deg, ${r.glow} 0%, rgba(14,19,21,0.95) 55%, rgba(14,19,21,0.98) 100%)`
          : "#0A0E10",
        boxShadow: unlocked ? `0 0 24px -6px ${r.glow}` : "none",
        clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)",
      }}
    >
      {/* Rarity ribbon */}
      <div
        className={`absolute top-0 right-0 ${isSm ? "text-[8px]" : "text-[9px]"} font-mono tracking-widest px-1.5 py-0.5`}
        style={{
          color: unlocked ? r.text : "#4A5658",
          background: unlocked ? `${r.glow}` : "transparent",
          borderLeft: `1px solid ${unlocked ? r.border : "#202B30"}`,
          borderBottom: `1px solid ${unlocked ? r.border : "#202B30"}`,
        }}
      >
        {r.label}
      </div>

      <div className="flex items-start gap-2.5 pt-3">
        <div
          className={`shrink-0 flex items-center justify-center ${isSm ? "w-8 h-8" : "w-10 h-10"} rounded-full`}
          style={{
            background: unlocked ? r.ring + "20" : "#141B1E",
            border: `1px solid ${unlocked ? r.ring : "#202B30"}`,
            boxShadow: unlocked ? `inset 0 0 10px ${r.glow}` : "none",
          }}
        >
          {unlocked ? (
            <Icon size={isSm ? 14 : 18} style={{ color: r.ring }} />
          ) : (
            <Lock size={isSm ? 12 : 14} className="text-ink-muted" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div
            className={`${isSm ? "text-[11px]" : "text-xs"} font-mono uppercase tracking-wider truncate`}
            style={{ color: unlocked ? r.text : "#70827D" }}
          >
            {badge.label}
          </div>
          <div
            className={`${isSm ? "text-[10px]" : "text-[11px]"} leading-tight mt-0.5 line-clamp-2`}
            style={{ color: unlocked ? "#A0B0AB" : "#4A5658" }}
          >
            {badge.desc}
          </div>
        </div>
      </div>
    </div>
  );
}
